/**
 * Capacitor Native Integration
 * Handles iOS/Android specific functionality
 */

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';

// Check if running in native app
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
export const isIOS = platform === 'ios';
export const isAndroid = platform === 'android';
export const isWeb = platform === 'web';

/**
 * Initialize native platform features
 */
export const initializeNative = async () => {
  if (!isNative) return;
  
  
  // Hide splash screen
  await SplashScreen.hide();
  
  // Configure status bar
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#09090b' });
  } catch (e) {
  }
  
  // Configure keyboard
  try {
    Keyboard.addListener('keyboardWillShow', () => {
      document.body.classList.add('keyboard-visible');
    });
    
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-visible');
    });
  } catch (e) {
  }
  
  // Handle back button (Android)
  if (isAndroid) {
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapacitorApp.exitApp();
      }
    });
  }
  
  // Handle app state changes
  CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    // You can pause audio playback here when app goes to background
    document.dispatchEvent(new CustomEvent('appStateChange', { detail: { isActive } }));
  });
  
  // Handle URL open (deep linking)
  CapacitorApp.addListener('appUrlOpen', ({ url }) => {
    // Handle deep links here
    document.dispatchEvent(new CustomEvent('deepLinkOpen', { detail: { url } }));
  });
};

/**
 * Haptic feedback helpers
 */
export const haptics = {
  // Light tap feedback
  tap: async () => {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (e) { /* Native API unavailable */ }
  },
  
  // Medium impact
  impact: async () => {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (e) { /* Native API unavailable */ }
  },
  
  // Heavy impact
  heavy: async () => {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (e) { /* Native API unavailable */ }
  },
  
  // Success notification
  success: async () => {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch (e) { /* Native API unavailable */ }
  },
  
  // Warning notification
  warning: async () => {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Warning });
    } catch (e) { /* Native API unavailable */ }
  },
  
  // Error notification
  error: async () => {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Error });
    } catch (e) { /* Native API unavailable */ }
  },
  
  // Selection changed
  selection: async () => {
    if (!isNative) return;
    try {
      await Haptics.selectionChanged();
    } catch (e) { /* Native API unavailable */ }
  },
};

/**
 * Keyboard helpers
 */
export const keyboard = {
  show: async () => {
    if (!isNative) return;
    try {
      await Keyboard.show();
    } catch (e) { /* Native API unavailable */ }
  },
  
  hide: async () => {
    if (!isNative) return;
    try {
      await Keyboard.hide();
    } catch (e) { /* Native API unavailable */ }
  },
};

/**
 * Status bar helpers
 */
export const statusBar = {
  setDark: async () => {
    if (!isNative) return;
    try {
      await StatusBar.setStyle({ style: Style.Dark });
    } catch (e) { /* Native API unavailable */ }
  },
  
  setLight: async () => {
    if (!isNative) return;
    try {
      await StatusBar.setStyle({ style: Style.Light });
    } catch (e) { /* Native API unavailable */ }
  },
  
  hide: async () => {
    if (!isNative) return;
    try {
      await StatusBar.hide();
    } catch (e) { /* Native API unavailable */ }
  },
  
  show: async () => {
    if (!isNative) return;
    try {
      await StatusBar.show();
    } catch (e) { /* Native API unavailable */ }
  },
};

/**
 * Get safe area insets (for notch/home indicator)
 */
export const getSafeAreaInsets = () => {
  const style = getComputedStyle(document.documentElement);
  return {
    top: parseInt(style.getPropertyValue('--sat') || '0', 10),
    right: parseInt(style.getPropertyValue('--sar') || '0', 10),
    bottom: parseInt(style.getPropertyValue('--sab') || '0', 10),
    left: parseInt(style.getPropertyValue('--sal') || '0', 10),
  };
};

export default {
  isNative,
  platform,
  isIOS,
  isAndroid,
  isWeb,
  initializeNative,
  haptics,
  keyboard,
  statusBar,
  getSafeAreaInsets,
};
